import React, { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import { showError } from "../../utils/helpers";
import { AccessKeyNormalRelation } from "../../api/models/access-key-model";
import api from "../../api";
import dayjs from "dayjs";
import { CircularProgress, Container } from "@mui/material";
import AccessKeyDetails from "../../components/AccessKeyDetails";
import { AccessGroupsData } from "../../api/models/access-groups";
import Logo from "../../components/Logo";

const AccessKey = () => {
  const labels = useAppSelector(selectLabels);
  const dispatch = useAppDispatch();

  const { uuid: accessKeyUuid } = useParams<{
    uuid: string;
  }>();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [accessKey, setAccessKey] =
    React.useState<AccessKeyNormalRelation | null>(null);
  const [isSameDate, setIsSameDate] = React.useState<boolean>(false);
  const [accessGroups, setAccessGroups] = React.useState<AccessGroupsData[]>(
    [],
  );
  const [visitorName, setVisitorName] = React.useState<string>("");
  const [verifiableUuid, setVerifiableUuid] = React.useState<string>("");
  const [showContent, setShowContent] = React.useState<boolean>(false);
  const [showErrorContent, setShowErrorContent] =
    React.useState<boolean>(false);

  const getAccessKey = useCallback(async () => {
    if (!accessKeyUuid) return;

    try {
      setIsLoading(true);
      setShowContent(false);
      setShowErrorContent(false);

      const acResponse =
        await api.invitationAccessKeyByUuidRequest(accessKeyUuid);
      const cAccessKey = acResponse?.[0];

      if (!cAccessKey) {
        setShowErrorContent(true);
        return;
      }

      const verId = cAccessKey?.verifiable_ids?.[0]?.uuid;
      let finalAccessKey: AccessKeyNormalRelation | null =
        cAccessKey as unknown as AccessKeyNormalRelation;
      let finalVerifiableUuid = verId ?? "";

      // Enrich from verifiable-id when possible, but don't fail the page if it fails.
      if (verId) {
        try {
          const response = await api.invitationByUuidRequest(verId);
          const enriched = response?.data?.[0]?.access_key_qr ?? null;
          if (enriched) {
            finalAccessKey = enriched;
            finalVerifiableUuid = verId;
          }
        } catch (enrichError) {
          console.error(enrichError);
        }
      }

      if (!finalVerifiableUuid) {
        finalVerifiableUuid =
          (cAccessKey as { qr_uuid?: string })?.qr_uuid ||
          cAccessKey?.uuid ||
          "";
      }

      if (!finalAccessKey) {
        setShowErrorContent(true);
        return;
      }

      setVerifiableUuid(finalVerifiableUuid);
      setAccessGroups(finalAccessKey?.access_groups ?? []);
      setAccessKey(finalAccessKey);

      const sameDate =
        dayjs(finalAccessKey?.valid_from).date() ===
          dayjs(finalAccessKey?.valid_until).date() &&
        dayjs(finalAccessKey?.valid_from).month() ===
          dayjs(finalAccessKey?.valid_until).month();
      setIsSameDate(sameDate);

      const isVerifiedVisitor =
        finalAccessKey?.verified_visitor?.name || null;
      const isOtherProvider =
        finalAccessKey?.one_time_visitor?.provider_name || null;
      const isProvider =
        finalAccessKey?.one_time_visitor?.provider?.name || null;
      const isOneTimeVisitor =
        finalAccessKey?.one_time_visitor?.name || null;
      const InvName = isVerifiedVisitor
        ? isVerifiedVisitor
        : isOtherProvider
          ? isOtherProvider
          : isProvider
            ? isProvider
            : isOneTimeVisitor
              ? isOneTimeVisitor
              : "";
      setVisitorName(InvName);
      setShowContent(true);
    } catch (error: any) {
      setShowErrorContent(true);
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    } finally {
      setIsLoading(false);
    }
  }, [accessKeyUuid, dispatch, labels.generic_error]);

  React.useEffect(() => {
    getAccessKey();
  }, [getAccessKey]);

  return (
    <Container
      maxWidth="sm"
      sx={{
        height: "100vh",
        paddingY: "20px",
        boxSizing: "border-box",
        overflow: "auto",
      }}
    >
      {isLoading && (
        <div className="w-100 vh-100 flex justify-content-center align-items-center">
          <CircularProgress />
        </div>
      )}
      {showContent && (
        <AccessKeyDetails
          isLoading={isLoading}
          verifiableUUID={verifiableUuid}
          visitorName={visitorName}
          currentAccessKey={accessKey}
          accessGroups={accessGroups}
          isSameDate={isSameDate}
        />
      )}
      {showErrorContent && !showContent && (
        <div className="w-100 h-100 flex flex-dir-col justify-content-center align-items-center gap-6">
          <Logo type={"passTrackLogo"} alt="passtrack" className="inv-logo" />
          <p className="mt-10">{labels.access_key_not_found}</p>
        </div>
      )}
    </Container>
  );
};

export default AccessKey;
